import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { projectsAPI, clientsAPI, reportTemplatesAPI, ReportTemplate } from '../../services/api';
import { Project, Client } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import './ProjectModal.css';

interface ProjectModalProps {
  onClose: () => void;
  onProjectCreated: (project: Project) => void;
  project?: Project;
}

const ProjectModal: React.FC<ProjectModalProps> = ({ onClose, onProjectCreated, project }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdminOrSuperuser = user?.role === 'superuser' || user?.role === 'admin';

  const [formData, setFormData] = useState({
    name: project?.name || '',
    description: project?.description || '',
    default_language: (project as any)?.default_language || 'en',
    client_id: (project as any)?.client_id || '',
    report_template_id: (project as any)?.report_template_id || null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Client selection state (for admins)
  const [clients, setClients] = useState<Client[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  // Template selection state
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Load clients for admin users
  useEffect(() => {
    if (isAdminOrSuperuser) {
      loadClients();
    }
  }, [isAdminOrSuperuser]);

  // Load templates (for all users)
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadClients = async () => {
    setClientsLoading(true);
    try {
      const response = await clientsAPI.getAll({ active: true });
      setClients(response.data.clients || response.data || []);
    } catch (err) {
      console.error('Failed to load clients:', err);
    } finally {
      setClientsLoading(false);
    }
  };

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const response = await reportTemplatesAPI.list();
      setTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const selectedTemplate = templates.find(t => t.id === Number(formData.report_template_id));
  const defaultTemplate = templates.find(t => t.is_default);

  const handleTemplateSelect = (templateId: number | null) => {
    setFormData(prev => ({ ...prev, report_template_id: templateId }));
    setShowTemplateDropdown(false);
  };

  const handlePreviewTemplate = async (templateId: number) => {
    setPreviewLoading(true);
    try {
      // Open preview in new tab using the API endpoint URL
      const baseUrl = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      // Open in new window
      window.open(
        `${baseUrl}/api/report-templates/${templateId}/preview`,
        '_blank',
        'width=1200,height=800,scrollbars=yes'
      );
    } catch (err) {
      console.error('Failed to preview template:', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Filter clients based on search
  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    (client.description && client.description.toLowerCase().includes(clientSearch.toLowerCase()))
  );

  const selectedClient = clients.find(c => c.id === Number(formData.client_id));

  const handleClientSelect = (clientId: number | null) => {
    setFormData(prev => ({ ...prev, client_id: clientId || '' }));
    setClientSearch('');
    setShowClientDropdown(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Clean up data: convert empty strings to null for numeric fields
      const cleanData = {
        ...formData,
        client_id: formData.client_id ? Number(formData.client_id) : null,
        report_template_id: formData.report_template_id ? Number(formData.report_template_id) : null,
      };

      let response;
      if (project) {
        response = await projectsAPI.update(project.id, cleanData);
      } else {
        response = await projectsAPI.create(cleanData);
      }
      
      onProjectCreated(response.data);
      onClose();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to save project');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{project ? 'Edit Project' : 'Create New Project'}</h2>
          <button onClick={onClose} className="modal-close">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && (
            <div className="error-message">
              <i className="fas fa-exclamation-triangle"></i>
              {error}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Project Name</label>
            <input
              type="text"
              name="name"
              className="form-input"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter project name"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('projects.description', 'Description')}</label>
            <textarea
              name="description"
              className="form-input"
              rows={4}
              value={formData.description}
              onChange={handleChange}
              placeholder={t('projects.descriptionPlaceholder', 'Enter project description')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('projects.defaultLanguage', 'Default Report Language')}</label>
            <select
              name="default_language"
              className="form-input"
              value={formData.default_language}
              onChange={handleChange}
            >
              <option value="en">{t('languages.english', 'English')}</option>
              <option value="de">{t('languages.german', 'German (Deutsch)')}</option>
              <option value="fr">{t('languages.french', 'French (Français)')}</option>
              <option value="it">{t('languages.italian', 'Italian (Italiano)')}</option>
            </select>
            <small className="form-hint">
              {t('projects.defaultLanguageHint', 'This language will be used for scheduled reports when no user-specific language is set')}
            </small>
          </div>

          {/* Client Selection - Only for admins and superusers */}
          {isAdminOrSuperuser && (
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-building" style={{ marginRight: '0.5rem' }}></i>
                {t('projects.client', 'Client / Customer')}
                <span className="optional-badge">{t('common.optional', 'Optional')}</span>
              </label>
              <div className="client-select-container">
                <div
                  className={`client-select-input ${showClientDropdown ? 'active' : ''}`}
                  onClick={() => setShowClientDropdown(!showClientDropdown)}
                >
                  {selectedClient ? (
                    <div className="selected-client">
                      <span className="client-name">{selectedClient.name}</span>
                      <button
                        type="button"
                        className="clear-client"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClientSelect(null);
                        }}
                        title={t('common.clear', 'Clear')}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ) : (
                    <span className="placeholder">{t('projects.selectClient', 'Select a client...')}</span>
                  )}
                  <i className={`fas fa-chevron-${showClientDropdown ? 'up' : 'down'} dropdown-arrow`}></i>
                </div>

                {showClientDropdown && (
                  <div className="client-dropdown">
                    <div className="client-search">
                      <i className="fas fa-search"></i>
                      <input
                        type="text"
                        placeholder={t('common.search', 'Search...')}
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    </div>
                    <div className="client-options">
                      {clientsLoading ? (
                        <div className="client-option loading">
                          <i className="fas fa-spinner fa-spin"></i>
                          {t('common.loading', 'Loading...')}
                        </div>
                      ) : filteredClients.length === 0 ? (
                        <div className="client-option empty">
                          {clientSearch ? t('common.noResults', 'No results found') : t('projects.noClients', 'No clients available')}
                        </div>
                      ) : (
                        filteredClients.map(client => (
                          <div
                            key={client.id}
                            className={`client-option ${Number(formData.client_id) === client.id ? 'selected' : ''}`}
                            onClick={() => handleClientSelect(client.id)}
                          >
                            <div className="client-option-info">
                              <span className="client-option-name">{client.name}</span>
                              {client.industry && (
                                <span className="client-option-industry">{client.industry}</span>
                              )}
                            </div>
                            {Number(formData.client_id) === client.id && (
                              <i className="fas fa-check"></i>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              <small className="form-hint">
                {t('projects.clientHint', 'Assign this project to a client for client portal access and reporting')}
              </small>
            </div>
          )}

          {/* Report Template Selection */}
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-file-alt" style={{ marginRight: '0.5rem' }}></i>
              {t('projects.reportTemplate', 'Report Template')}
              <span className="optional-badge">{t('common.optional', 'Optional')}</span>
            </label>
            <div className="template-select-container">
              <div
                className={`template-select-input ${showTemplateDropdown ? 'active' : ''}`}
                onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
              >
                {selectedTemplate ? (
                  <div className="selected-template">
                    <div className="template-info">
                      <span className="template-name">{selectedTemplate.name}</span>
                      {selectedTemplate.is_system && (
                        <span className="system-badge">{t('common.system', 'System')}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="clear-template"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTemplateSelect(null);
                      }}
                      title={t('common.clear', 'Clear')}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ) : (
                  <span className="placeholder">
                    {defaultTemplate
                      ? t('projects.useDefaultTemplate', 'Use default template: {{name}}', { name: defaultTemplate.name })
                      : t('projects.selectTemplate', 'Select a template...')}
                  </span>
                )}
                <i className={`fas fa-chevron-${showTemplateDropdown ? 'up' : 'down'} dropdown-arrow`}></i>
              </div>

              {showTemplateDropdown && (
                <div className="template-dropdown">
                  <div className="template-options">
                    {templatesLoading ? (
                      <div className="template-option loading">
                        <i className="fas fa-spinner fa-spin"></i>
                        {t('common.loading', 'Loading...')}
                      </div>
                    ) : templates.length === 0 ? (
                      <div className="template-option empty">
                        {t('projects.noTemplates', 'No templates available')}
                      </div>
                    ) : (
                      templates.map(template => (
                        <div
                          key={template.id}
                          className={`template-option ${Number(formData.report_template_id) === template.id ? 'selected' : ''} ${template.is_default ? 'default' : ''}`}
                          onClick={() => handleTemplateSelect(template.id)}
                        >
                          <div className="template-option-info">
                            <span className="template-option-name">
                              {template.name}
                              {template.is_default && (
                                <span className="default-badge">{t('common.default', 'Default')}</span>
                              )}
                              {template.is_system && (
                                <span className="system-badge">{t('common.system', 'System')}</span>
                              )}
                            </span>
                            {template.description && (
                              <span className="template-option-description">{template.description}</span>
                            )}
                          </div>
                          <div className="template-option-actions">
                            <button
                              type="button"
                              className="preview-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePreviewTemplate(template.id);
                              }}
                              title={t('common.preview', 'Preview')}
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            {Number(formData.report_template_id) === template.id && (
                              <i className="fas fa-check"></i>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <small className="form-hint">
              {t('projects.templateHint', 'Select a template for HTML reports. If none selected, the system default will be used.')}
            </small>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-outline">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  {project ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i>
                  {project ? 'Update Project' : 'Create Project'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectModal;